import React, { useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Grid3x3, Hourglass, Minus } from 'lucide-react';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useConversionReport } from '../../../hooks/pipelineTracker/useConversionReport';
import {
  CONVERSION_COLUMNS,
  CONVERSION_ROWS,
  cellOf,
  conversionHistoryCapped,
  gapPoints,
  isForward,
  type ConversionCell,
  type ConversionMatrix,
  type ConversionReportResult,
} from '../../../hooks/pipelineTracker/conversionContract';
import { Panel, Pill, num } from './pipelineParts';
import { InfoTip } from './trackerKit';

/**
 * Stage-to-stage conversion rates, per broker against the business average.
 *
 * FOLLOWED, NOT ESTIMATED. Every rate comes from seeing the same deal reference in more than one
 * weekly upload (reports/conversion.ts on the server says exactly which deals count and why). One
 * snapshot cannot produce a rate, so under four snapshots this section says how many it has and
 * shows none: a "Building" panel with its progress, not an entry in the "Coming when the data is
 * available" list, because it waits on the calendar rather than on a person, and naming an owner
 * would send someone chasing an input that does not exist.
 *
 * THE BUSINESS AVERAGE IS ALWAYS EVERY DEAL. Picking a broker changes the cells to that broker's
 * rates and adds the gap to the business, in percentage points, with an arrow AND the words above
 * or below, so the direction survives a colour-blind reader and a greyscale print.
 *
 * NO PERIOD. The history is exactly as long as the run of uploads, which the badge states, so the
 * page's Period control does not narrow it; the scope line at the top of the tab says Period sets
 * the movements, cohort band and broker columns only.
 */

const formatDate = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const pct = (v: number): string => `${Number.isInteger(v) ? v : v.toFixed(1)}%`;

/**
 * Shading rises with the rate, in the brand blue gradient (founder, 5 Oct 2026: add colour): a low
 * rate is a faint wash, a high one a strong fill. A greyed cell carries none: a small sample is not
 * a mild result. Exported for the tests.
 */
export function heat(cell: ConversionCell): React.CSSProperties {
  if (cell.ratePct === null || cell.lowSample) return {};
  const t = Math.min(100, Math.max(0, cell.ratePct)) / 100;
  const strong = (0.1 + t * 0.55).toFixed(2);
  const soft = (0.05 + t * 0.3).toFixed(2);
  return { background: `linear-gradient(135deg, rgba(59,130,246,${strong}), rgba(37,99,235,${soft}))` };
}

const Gap: React.FC<{ points: number; muted: boolean }> = ({ points, muted }) => {
  const abs = Math.abs(points);
  const [Icon, words, tone] =
    points > 0
      ? [ArrowUp, `${abs} pts above`, 'text-emerald-400']
      : points < 0
        ? [ArrowDown, `${abs} pts below`, 'text-red-400']
        : [Minus, 'level', 'text-gray-400'];
  return (
    <span
      className={`mt-0.5 flex items-center justify-end gap-0.5 text-[0.6875rem] font-medium ${muted ? 'text-gray-500' : tone}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {words}
    </span>
  );
};

const RateCell: React.FC<{
  cell: ConversionCell | null;
  businessCell: ConversionCell | null;
  comparing: boolean;
  lowSampleBelow: number;
}> = ({ cell, businessCell, comparing, lowSampleBelow }) => {
  if (!cell || cell.ratePct === null) {
    return (
      <td className="px-3 py-2 text-right text-gray-500" title="No deal was followed from this stage">
        -
      </td>
    );
  }
  const points = comparing ? gapPoints(cell, businessCell) : null;
  // n (the deals behind the rate) lives in the hover title and in screen-reader text, not on the
  // cell (founder, 5 Oct 2026: "why are they n= values?"). The header's info tip explains it once.
  const behind = `${num(cell.converted)} of ${num(cell.denominator)} deals`;
  return (
    <td
      className={`px-3 py-2 text-right tabular-nums ${cell.lowSample ? 'text-gray-500' : 'text-gray-100'}`}
      style={heat(cell)}
      data-low-sample={cell.lowSample ? 'true' : undefined}
      title={`${behind} (n = ${num(cell.denominator)})`}
    >
      <span className="font-semibold">{pct(cell.ratePct)}</span>
      <span className="sr-only">, {behind}</span>
      {cell.lowSample && (
        <span className="block text-[0.6875rem] text-gray-500" data-testid="low-sample-marker">
          under {num(lowSampleBelow)} deals
        </span>
      )}
      {points !== null && <Gap points={points} muted={cell.lowSample} />}
    </td>
  );
};

const Matrix: React.FC<{
  matrix: ConversionMatrix;
  business: ConversionMatrix;
  comparing: boolean;
  lowSampleBelow: number;
}> = ({ matrix, business, comparing, lowSampleBelow }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-sm">
      <thead className="text-[0.6875rem] uppercase tracking-wider text-gray-400">
        <tr>
          <th className="px-3 py-2 font-semibold">From \ to</th>
          {CONVERSION_COLUMNS.map((c) => (
            <th key={c.stage} className="px-3 py-2 text-right font-semibold">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700/50">
        {CONVERSION_ROWS.map((r) => (
          <tr key={r.stage}>
            <th scope="row" className="px-3 py-2 font-medium text-gray-200">{r.label}</th>
            {CONVERSION_COLUMNS.map((c) =>
              isForward(r.stage, c.stage) ? (
                <RateCell
                  key={c.stage}
                  cell={cellOf(matrix, r.stage, c.stage)}
                  businessCell={cellOf(business, r.stage, c.stage)}
                  comparing={comparing}
                  lowSampleBelow={lowSampleBelow}
                />
              ) : (
                <td key={c.stage} className="px-3 py-2 text-right text-gray-700" aria-hidden="true" />
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TITLE = 'Stage-to-stage conversion rates';

export const ConversionPanel: React.FC<{
  result: ConversionReportResult | null;
  loading: boolean;
  error: string | null;
  /** The broker the page is scoped to, if any, so Compare opens on them. */
  scopedAdviserId?: string;
  /** The panel's accent: its group's colour on the jump bar. Brand blue by default. */
  accent?: string;
  /** The scope chip: the history is as long as the uploads, never the period. */
  chip?: React.ReactNode;
}> = ({ result, loading, error, scopedAdviserId, accent, chip }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const brokers = result?.status === 'ok' ? result.brokers : [];

  // Follow the page's broker chip when it names someone in the breakdown. Clearing the chip goes
  // back to the business average rather than leaving a stale comparison on screen.
  useEffect(() => {
    if (scopedAdviserId === undefined) setPicked(null);
    else if (brokers.some((b) => b.adviserKey === scopedAdviserId)) setPicked(scopedAdviserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedAdviserId, result]);

  if (loading) {
    return (
      <Panel title={TITLE} icon={Grid3x3} accent={accent}>
        <SkeletonTable rows={6} columns={7} />
      </Panel>
    );
  }
  if (error || !result) {
    return (
      <Panel title={TITLE} icon={Grid3x3} accent={accent}>
        <p className="text-sm text-amber-300">{error ?? 'Stage-to-stage conversion could not be loaded.'}</p>
      </Panel>
    );
  }

  if (result.status === 'building') {
    return (
      <Panel
        title={TITLE}
        icon={Grid3x3}
        accent={accent}
        badge={<Pill tone="brand"><Hourglass className="h-3 w-3" />Building</Pill>}
        blurb="Each rate follows the same deal from one weekly upload to the next. It needs history, and history only comes from uploads."
      >
        <div className="rounded-lg border border-dashed border-blue-500/30 bg-blue-600/[0.06] px-4 py-3 text-sm text-blue-100">
          <b className="font-semibold text-blue-300">
            {num(result.snapshotCount)} of {num(result.snapshotsNeeded)} weekly snapshots so far.
          </b>{' '}
          Rates appear once {num(result.snapshotsNeeded)} Performance uploads let VALUTTEN follow each
          deal from stage to stage. Nothing is estimated in the meantime.
          {result.firstAsAtDate !== null && result.asAtDate !== null && (
            <span className="mt-1 block text-xs text-blue-200/80">
              Snapshots from {formatDate(result.firstAsAtDate)} to {formatDate(result.asAtDate)}.
            </span>
          )}
        </div>
      </Panel>
    );
  }

  const selected = picked === null ? null : brokers.find((b) => b.adviserKey === picked) ?? null;
  const matrix = selected ?? result.business;
  const capped = conversionHistoryCapped(result);

  return (
    <Panel
      title={TITLE}
      icon={Grid3x3}
      accent={accent}
      badge={
        <>
          <InfoTip label="the deal counts behind each rate">
            n is the number of deals behind each rate: the deals seen at the row&apos;s stage in one
            upload and seen again in a later one. Hover a cell for its count. A greyed cell rests on fewer
            than {num(result.lowSampleBelow)} deals, too few to read as a result.
          </InfoTip>
          {chip}
          <Pill tone="brand">
            <Grid3x3 className="h-3 w-3" />
            {num(result.weeksOfHistory)} {result.weeksOfHistory === 1 ? 'week' : 'weeks'} of history &middot;{' '}
            {capped
              ? `latest ${num(result.snapshotCount)} of ${num(result.snapshotsAvailable)} snapshots`
              : `${num(result.snapshotCount)} snapshots`}
          </Pill>
        </>
      }
      blurb="Share of deals seen at the row's stage that were later seen at or beyond the column's stage, following each deal by its reference across every weekly upload. Pick a broker to see their rates and the gap to the business average."
    >
      <div className="mb-3 flex flex-wrap items-center gap-2" role="group" aria-label="Compare">
        <span className="mr-1 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-gray-400">Compare</span>
        {[{ key: null as string | null, name: 'Business average' }, ...brokers.map((b) => ({ key: b.adviserKey as string | null, name: b.adviserName }))].map(
          (option) => {
            const on = picked === option.key || (option.key === null && selected === null);
            return (
              <button
                key={option.key ?? '__business'}
                type="button"
                aria-pressed={on}
                onClick={() => setPicked(option.key)}
                // The kit's chip shape (trackerKit Chip): blue when chosen, muted otherwise.
                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                  on
                    ? 'border-blue-500/50 bg-blue-500/20 text-blue-100 shadow-[0_0_0.75rem_rgba(37,99,235,0.25)]'
                    : 'border-gray-700 bg-white/[0.04] text-gray-400 hover:border-gray-500 hover:text-gray-200'
                }`}
              >
                {option.name}
              </button>
            );
          },
        )}
      </div>

      <Matrix matrix={matrix} business={result.business} comparing={selected !== null} lowSampleBelow={result.lowSampleBelow} />

      <div className="mt-3 space-y-1.5 text-xs leading-relaxed text-gray-400">
        <p>
          {num(result.dealsFollowed)} deals followed across {num(result.snapshotCount)} snapshots,{' '}
          {formatDate(result.firstAsAtDate)} to {formatDate(result.asAtDate)}.
          {capped &&
            ` History is capped: these rates read the most recent ${num(result.snapshotCount)} of ${num(result.snapshotsAvailable)} published snapshots (at most ${num(result.snapshotCap)}, half a year of weekly uploads). Older uploads are kept but not read here.`}
          {selected !== null && ` ${num(selected.dealsInRates)} of them are in ${selected.adviserName}'s rates, credited to whoever owned each deal when it sat at the row's stage.`}
        </p>
        <p>
          Stages passed between two uploads are invisible. A deal seen at Lead and next seen at
          Submission counts as reaching Meeting, Analysis and Proposal, but never appears in those
          rows. Uploads so far are up to {num(result.longestGapDays)} days apart; the more often you
          upload, the less this misses.
        </p>
        <p>
          A deal counts in a row only once a later upload has seen it again, so deals first seen in
          the latest upload, and deals that left the export, are in no rate either way. A greyed cell
          rests on fewer than {num(result.lowSampleBelow)} deals. Hover any cell for the deals behind it.
        </p>
        {result.ungroupedStageLabels.length > 0 && (
          <p>
            {result.ungroupedStageLabels.join(', ')}{' '}
            {result.ungroupedStageLabels.length === 1 ? 'sits' : 'sit'} outside these seven stages, so
            deals there are in no rate.
          </p>
        )}
        <details>
          <summary className="cursor-pointer text-gray-400 hover:text-gray-200">What each stage covers</summary>
          <ul className="mt-2 space-y-1">
            {result.stageGroups.map((g) => (
              <li key={g.stage}>
                <b className="font-semibold text-gray-300">{g.label}:</b>{' '}
                {g.stageLabels.length > 0 ? g.stageLabels.join(', ') : 'no stage in your stage list'}
              </li>
            ))}
          </ul>
        </details>
      </div>
    </Panel>
  );
};

const ConversionSection: React.FC<{ companyId: string; scopedAdviserId?: string; accent?: string; chip?: React.ReactNode }> = ({
  companyId, scopedAdviserId, accent, chip,
}) => {
  const { result, loading, error } = useConversionReport(companyId);
  return (
    <ConversionPanel result={result} loading={loading} error={error} scopedAdviserId={scopedAdviserId} accent={accent} chip={chip} />
  );
};

export default ConversionSection;
